import Konva from "konva";
import { Context } from "konva/lib/Context";
import {
    ShadowGeometry,
    composeShadowRGBA,
    parseShadowColor,
    rasterizeShadowAlpha,
    resolveShadowGeometry,
} from "./shadowGeometry";

export interface LvglShadowParams {
    /** LVGL shadow_width — the box-blur window. 0 means "no shadow at all". */
    shadowWidth?: number | null;
    /** LVGL shadow_spread — expands the shadow's base rectangle. */
    shadowSpread?: number | null;
    ofsX?: number | null;
    ofsY?: number | null;
    color?: string | null;
    /** LVGL shadow_opa, 0-255. Unset resolves to LV_OPA_COVER. */
    opa?: number | null;
    /** The object's effective radius — same value the background/border use. */
    radius?: number | null;
}

export interface LvglShadowConfig extends Konva.ShapeConfig, LvglShadowParams {
    /**
     * Lazily reads the object's box, so the shadow can never go stale against
     * a widget that resizes its main rect directly. Falls back to this shape's
     * own width/height when omitted.
     */
    sizeProvider?: () => { width: number; height: number };
}

/**
 * LvglShadowShape - a Konva Shape that draws an LVGL 7 box shadow BEHIND the
 * object's background and border.
 *
 * It deliberately does not use Konva's native `shadowBlur`: that is a Gaussian
 * blur applied to the drawn shape, and neither its falloff nor its extent match
 * LVGL. Instead the shadow is a separate shape whose sceneFunc blits a mask
 * rasterized by shadowGeometry.ts — LVGL's own rounded-rect mask plus its
 * separable single-pass box blur, which is what produces LVGL's linear falloff.
 *
 * The box it shadows is the OBJECT's box, read through `sizeProvider` so it can
 * never go stale; the drawn bitmap extends outside that box by whatever
 * spread/offset/width demand (see getSelfRect).
 */
export default class LvglShadowShape extends Konva.Shape<LvglShadowConfig> {
    private _params: Required<{ [K in keyof LvglShadowParams]: LvglShadowParams[K] }>;
    private _sizeProvider?: () => { width: number; height: number };
    private _canvas: HTMLCanvasElement | null = null;
    private _cacheKey = "";

    constructor(config: LvglShadowConfig) {
        super({
            ...config,
            // Decoration only: never hit-testable, never selectable. No Konva
            // shadow attribute is set here either — this shape IS the shadow,
            // and Konva's native Gaussian one plays no part in drawing it.
            listening: false,
            strokeWidth: 0,
            hitStrokeWidth: 0,
            perfectDrawEnabled: false,
        });

        this._sizeProvider = config.sizeProvider;

        this._params = {
            shadowWidth: config.shadowWidth ?? 0,
            shadowSpread: config.shadowSpread ?? 0,
            ofsX: config.ofsX ?? 0,
            ofsY: config.ofsY ?? 0,
            color: config.color ?? "#000000",
            opa: config.opa ?? null,
            radius: config.radius ?? 0,
        };

        this.sceneFunc((context: Context) => {
            const geom = this._geometry();
            if (!geom.visible) return;

            const canvas = this._bitmap(geom);
            if (!canvas) return;

            // Drawn in object-local units; the bitmap may be rasterized at a
            // higher device resolution, so give drawImage an explicit target
            // size rather than relying on its natural pixel size.
            context.drawImage(
                canvas as unknown as CanvasImageSource,
                geom.bounds.x,
                geom.bounds.y,
                geom.bounds.width,
                geom.bounds.height,
            );
        });

        // No hit region at all — the shadow must never steal a click from the
        // object it belongs to, nor from anything it overlaps.
        this.hitFunc(() => {});
    }

    /** The object's box — from the provider when one was given. */
    private _box(): { width: number; height: number } {
        return this._sizeProvider?.() ?? { width: this.width(), height: this.height() };
    }

    private _geometry(): ShadowGeometry {
        const box = this._box();
        return resolveShadowGeometry({
            width: box.width,
            height: box.height,
            radius: this._params.radius,
            shadowWidth: this._params.shadowWidth,
            shadowSpread: this._params.shadowSpread,
            ofsX: this._params.ofsX,
            ofsY: this._params.ofsY,
            opa: this._params.opa,
        });
    }

    /**
     * Device pixels per object unit to rasterize at. Rasterizing at the stage's
     * current zoom keeps the falloff crisp when zoomed in (LVGL's blur is
     * defined in device pixels, so scaling the mask before blurring is more
     * correct than blitting a 1:1 bitmap and letting the browser interpolate).
     * Capped so a deep zoom on a large widget cannot allocate an absurd buffer.
     */
    private _rasterScale(geom: ShadowGeometry): number {
        const abs = this.getAbsoluteScale?.() ?? { x: 1, y: 1 };
        let scale = Math.min(3, Math.max(1, Math.max(abs.x || 1, abs.y || 1)));
        const area = Math.max(1, geom.bounds.width * geom.bounds.height);
        const MAX_PIXELS = 4_000_000;
        if (area * scale * scale > MAX_PIXELS) scale = Math.max(1, Math.sqrt(MAX_PIXELS / area));
        return Math.round(scale * 4) / 4; // quantized so tiny zoom jitter doesn't re-rasterize
    }

    /** Rasterize (or reuse) the shadow bitmap for the current parameters. */
    private _bitmap(geom: ShadowGeometry): HTMLCanvasElement | null {
        const scale = this._rasterScale(geom);
        const key = [
            geom.core.x, geom.core.y, geom.core.width, geom.core.height,
            geom.radius, geom.blur, geom.opa, this._params.color, scale,
        ].join("|");
        if (this._canvas && this._cacheKey === key) return this._canvas;

        const map = rasterizeShadowAlpha(geom, scale);
        const rgba = composeShadowRGBA(map, parseShadowColor(this._params.color), geom.opa);

        const canvas = (this._canvas ??= Konva.Util.createCanvasElement());
        canvas.width = map.width;
        canvas.height = map.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        const image = ctx.createImageData(map.width, map.height);
        image.data.set(rgba);
        ctx.putImageData(image, 0, 0);

        this._cacheKey = key;
        return canvas;
    }

    /**
     * Merge new LVGL shadow values. The single entry point — callers push
     * resolved effective style values in, so there are no defaults duplicated
     * here and no widget-specific property lists anywhere.
     */
    setShadowParams(params: LvglShadowParams): this {
        let changed = false;
        for (const [key, value] of Object.entries(params) as [keyof LvglShadowParams, never][]) {
            if (this._params[key] !== value) {
                this._params[key] = value;
                changed = true;
            }
        }
        if (changed) this._invalidateShadow();
        return this;
    }

    /** Current parameter snapshot — for tests and debugging. */
    getShadowParams(): LvglShadowParams {
        return { ...this._params };
    }

    /** The resolved geometry for the current size and parameters. */
    getShadowGeometry(): ShadowGeometry {
        return this._geometry();
    }

    /**
     * The drawn extent, which is larger than the object's box whenever the
     * shadow spreads or is offset. Konva uses this for the group's client rect
     * and for render bounds — the object's own x/y/width/height (and therefore
     * everything exported) are untouched. ObjectGroup separately pins the
     * Transformer to the main rect, so selection handles stay on the object.
     */
    getSelfRect() {
        const box = this._box();
        const geom = this._geometry();
        if (!geom.visible) return { x: 0, y: 0, width: box.width, height: box.height };
        const x1 = Math.min(0, geom.bounds.x);
        const y1 = Math.min(0, geom.bounds.y);
        const x2 = Math.max(box.width, geom.bounds.x + geom.bounds.width);
        const y2 = Math.max(box.height, geom.bounds.y + geom.bounds.height);
        return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
    }

    setAttrs(config: any): this {
        super.setAttrs(config);
        const known: (keyof LvglShadowParams)[] = [
            "shadowWidth", "shadowSpread", "ofsX", "ofsY", "color", "opa", "radius",
        ];
        for (const key of known) {
            if (key in config) (this._params as any)[key] = config[key];
        }
        this._invalidateShadow();
        return this;
    }

    private _invalidateShadow(): void {
        this._cacheKey = "";
        this.getLayer()?.batchDraw();
    }
}
